/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { ApiResponse } from '../types/api';
import { g17ecoApi, transformResponse } from './g17ecoApi';
import { JobStatus } from '../types/background-jobs';
import { DataPeriods } from '@utils/dataPeriods';

export type StatusFilter = 'updated' | 'verified' | 'rejected';

export const ALL_STATUSES: StatusFilter[] = ['updated', 'verified', 'rejected'];

// Issue type discriminator - these are the check types that can be run
export enum DataQualityIssueType {
  MissingRequiredColumns = 'missing_required_columns',
  DecimalPrecision = 'decimal_precision',
  // Future types:
  // VariationThreshold = 'variation_threshold',
}

export const ALL_CHECK_TYPES: DataQualityIssueType[] = [
  DataQualityIssueType.MissingRequiredColumns,
  DataQualityIssueType.DecimalPrecision,
];

export const DATA_QUALITY_CHECK_LABELS: Record<DataQualityIssueType, string> = {
  [DataQualityIssueType.MissingRequiredColumns]: 'Missing Required Columns',
  [DataQualityIssueType.DecimalPrecision]: 'Decimal Precision',
};

// Generic data quality issue structure
export interface DataQualityIssue {
  issueType: DataQualityIssueType;
  utrvId: string;
  surveyId: string;
  initiativeId: string;
  initiativeName: string;
  utrCode: string;
  utrName: string;
  effectiveDate: string;
  status: string;
  rowIndex: number;
  columns: string[]; // Generic field for column names (missing, invalid, etc.)
}

// Generic response structure for data quality checks
export interface DataQualityResponse {
  totalIssues: number;
  results: DataQualityIssue[];
  filters?: DataQualityFilters;
}

export interface DataQualityJob {
  _id: string;
  status: JobStatus;
  created: string;
  completedDate?: string;
}

// Filter configuration that can be passed to background job
export interface DataQualityFilters {
  statuses: StatusFilter[];
  checkTypes: DataQualityIssueType[];
  initiativeIds?: string[];
  years?: string[];
  months?: string[];
  periods?: DataPeriods[];
}

interface CreateJobRequest {
  initiativeId: string;
  filters?: Partial<DataQualityFilters>;
}

interface CreateJobResponse {
  jobId: string;
  status: string;
}

const DATA_QUALITY_TAG = 'data-quality';
const DATA_QUALITY_JOB_TAG = 'data-quality-job';

export const dataQualityApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: [DATA_QUALITY_TAG, DATA_QUALITY_JOB_TAG],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getLatestDataQualityJob: builder.query<DataQualityJob | null, string>({
        transformResponse: (response: ApiResponse<{ job: DataQualityJob | null }>) => transformResponse(response).job,
        query: (initiativeId) => ({
          url: `/initiatives/${initiativeId}/data-quality/jobs`,
          method: 'get',
        }),
        providesTags: [DATA_QUALITY_JOB_TAG],
      }),
      createDataQualityJob: builder.mutation<CreateJobResponse, CreateJobRequest>({
        transformResponse: (response: ApiResponse<CreateJobResponse>) => transformResponse(response),
        query: ({ initiativeId, filters }) => ({
          url: `/initiatives/${initiativeId}/data-quality/jobs`,
          method: 'post',
          data: filters ? { filters } : undefined,
        }),
        invalidatesTags: [DATA_QUALITY_JOB_TAG],
      }),
      getDataQualityJobResults: builder.query<DataQualityResponse, { initiativeId: string; jobId: string }>({
        transformResponse: (response: ApiResponse<DataQualityResponse>) => transformResponse(response),
        query: ({ initiativeId, jobId }) => ({
          url: `/initiatives/${initiativeId}/data-quality/jobs/${jobId}/results`,
          method: 'get',
        }),
        providesTags: [DATA_QUALITY_TAG],
      }),
    }),
  });

export const {
  useGetLatestDataQualityJobQuery,
  useCreateDataQualityJobMutation,
  useGetDataQualityJobResultsQuery,
} = dataQualityApi;
